/// <amd-module name="Agile/Common/Utils/QueuedRequestManager/QueuedRequestManagerSagaUtils" />
import { QueuedRequestManager } from "Agile/Common/Utils/QueuedRequestManager/QueuedRequestManager";
import { IRequestHandle, IRequestMetadata } from "Agile/Common/Utils/QueuedRequestManager/QueuedRequestManager.types";
import { effects, SagaIterator } from "redux-saga";

/**
 * Parameter structure to be passed to queueRequestSaga, containing the
 * necessary context and request info.
 */
export interface IQueuedSagaRequest<TResult = undefined, TArgs = undefined> {
    requestManager: QueuedRequestManager;
    requestMetadata: IRequestMetadata<TResult>;
    saga: (args?: TArgs) => SagaIterator;
    sagaArgs?: TArgs;
}

/**
 * Uses the QueuedRequestManager to queue a request which executes a saga. The
 * value returned from the request saga will be returned. If an error occurred,
 * an exception is raised. If a QueueClearedDueToRejectionError is thrown, the
 * request was never executed because a previous request cancelled everything
 * after it in the queue.
 *
 * @param args parameters controlling the behavior of the request
 */
export function* queueRequestSaga<TResult, TArgs>(args: IQueuedSagaRequest<TResult, TArgs>): SagaIterator {
    const requestHandle: IRequestHandle<TResult> = (yield effects.call(
        [args.requestManager, args.requestManager.waitForPredecessors],
        args.requestMetadata
    )) as any;
    try {
        const sagaResult: TResult = (yield effects.call(args.saga, args.sagaArgs)) as any;
        return yield effects.call([requestHandle, requestHandle.registerFinished], sagaResult);
    } catch (e) {
        yield effects.call([requestHandle, requestHandle.registerFailed], e);
        throw e;
    }
}
